'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { X, AlertTriangle, ShieldAlert, Zap, Anchor, CheckCircle2, Building2, MapPin, TrendingUp, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface Alert {
  id: string;
  type: 'sanctions' | 'cyber' | 'maritime' | 'instability' | 'supply';
  title: string;
  body: string;
  severity: 'critical' | 'high' | 'medium';
  time: string;
  dismissed: boolean;
  affectedExposure?: string;
  clientRelevance?: string;
  recommendedAction?: string;
  operationalImpact?: string;
}

const initialAlerts: Alert[] = [
  {
    id: 'alert-001',
    type: 'sanctions',
    title: 'Sanctions Match Detected — OFAC SDN',
    body: 'Entity "Kaveh Petrochemical Co." matches new OFAC SDN designation. Immediate compliance review required before any further transactions.',
    severity: 'critical',
    time: '14:07',
    dismissed: false,
    affectedExposure: '2 Acme Corp Tier-1 suppliers in petrochemical supply chain',
    clientRelevance: 'Direct exposure — supplier screening required within 24 hours',
    recommendedAction: 'Suspend transactions with flagged entities. Initiate compliance review and generate audit log.',
    operationalImpact: 'Potential procurement disruption if suppliers cannot be cleared',
  },
  {
    id: 'alert-002',
    type: 'maritime',
    title: 'Route Disruption — Red Sea / Houthi Activity',
    body: 'Vessel MV Stellaris rerouted via Cape of Good Hope following Houthi drone activity. ETA extended by 14 days. Insurance premium surcharge +22%.',
    severity: 'high',
    time: '13:54',
    dismissed: false,
    affectedExposure: '1 active Acme Corp shipment on affected Red Sea corridor',
    clientRelevance: 'Logistics delay confirmed — downstream delivery windows impacted',
    recommendedAction: 'Notify downstream customers of revised ETAs. Review insurance coverage for rerouted vessels.',
    operationalImpact: '+14 days transit, +22% freight insurance cost',
  },
  {
    id: 'alert-003',
    type: 'cyber',
    title: 'APT41 IOC Alert — Financial Infrastructure',
    body: 'New indicators of compromise for APT41 published by CISA. 3 monitored IP ranges match your network exposure profile. SWIFT messaging infrastructure targeted.',
    severity: 'high',
    time: '13:28',
    dismissed: false,
    affectedExposure: 'APAC financial operations and banking integrations',
    clientRelevance: 'Network exposure profile overlap detected — immediate review advised',
    recommendedAction: 'Review network segmentation. Block flagged IP ranges. Notify security team.',
    operationalImpact: 'Potential disruption to APAC financial transaction processing',
  },
  {
    id: 'alert-004',
    type: 'instability',
    title: 'Escalation Warning — Taiwan Strait',
    body: 'PLA announced 72-hour naval exercise zone covering northern Taiwan Strait corridor. Shipping advisories issued. Semiconductor supply chain risk elevated.',
    severity: 'high',
    time: '13:41',
    dismissed: false,
    affectedExposure: 'East Asia electronics sourcing — 3 Acme Corp component suppliers in Taiwan',
    clientRelevance: 'Procurement risk elevated for Q3 electronics components',
    recommendedAction: 'Assess alternative sourcing options. Monitor escalation probability over next 7 days.',
    operationalImpact: 'Potential component shortages if strait closure extends beyond 72 hours',
  },
  {
    id: 'alert-005',
    type: 'supply',
    title: 'Supplier Disruption — Chile Copper Operations',
    body: 'Tier-2 supplier Codelco Andina halted operations due to industrial strike. 3 monitored procurement contracts affected. Copper price impact expected.',
    severity: 'medium',
    time: '12:30',
    dismissed: false,
    affectedExposure: '3 Acme Corp procurement contracts for copper components',
    clientRelevance: 'Tier-2 supplier disruption — assess buffer stock levels',
    recommendedAction: 'Review buffer stock. Identify alternative copper suppliers. Update procurement forecasts.',
    operationalImpact: 'Estimated 2–3 week delay on copper-dependent manufacturing lines',
  },
  {
    id: 'alert-006',
    type: 'sanctions',
    title: 'EU Sanctions Package XIV — Russia Energy',
    body: 'Russia energy sector expansion — 14 new entities designated. Cross-reference with monitored supplier list recommended. Energy commodity exposure elevated.',
    severity: 'high',
    time: '12:55',
    dismissed: false,
    affectedExposure: 'Energy commodity procurement and Russian counterparty exposure',
    clientRelevance: 'Supplier list cross-reference required — potential indirect exposure',
    recommendedAction: 'Run full supplier screening against new EU designations. Update compliance records.',
    operationalImpact: 'Compliance burden increase — audit-ready documentation required',
  },
];

const alertIcons: Record<Alert['type'], React.ComponentType<{ size?: number; className?: string }>> = {
  sanctions: ShieldAlert, cyber: Zap, maritime: Anchor, instability: AlertTriangle, supply: AlertTriangle,
};

const alertStyle: Record<Alert['severity'], { border: string; icon: string; badge: string }> = {
  critical: { border: 'border-l-danger', icon: 'text-danger', badge: 'bg-danger/10 text-danger' },
  high: { border: 'border-l-warning', icon: 'text-warning', badge: 'bg-warning/10 text-warning' },
  medium: { border: 'border-l-info', icon: 'text-info', badge: 'bg-info/10 text-info' },
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>(initialAlerts);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const dismiss = (id: string) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: true } : a));
  const dismissAll = () => setAlerts(prev => prev.map(a => ({ ...a, dismissed: true })));
  const restore = (id: string) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: false } : a));

  const active = alerts.filter(a => !a.dismissed);
  const dismissed = alerts.filter(a => a.dismissed);

  return (
    <AppLayout activeRoute="/alerts">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Alerts</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{active.length} active alert{active.length !== 1 ? 's' : ''} · company-specific exposure notifications</p>
          </div>
          <div className="flex items-center gap-3">
            {active.length > 0 && (
              <button onClick={dismissAll} className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-md px-3 py-2 hover:bg-muted transition-colors">
                <CheckCircle2 size={13} />
                Clear All
              </button>
            )}
            <Link
              href="/settings"
              className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all"
            >
              Configure Rules
            </Link>
          </div>
        </div>

        {active.length === 0 && (
          <div className="card-elevated rounded-xl border border-border p-12 text-center">
            <CheckCircle2 size={32} className="text-success mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">All clear</p>
            <p className="text-xs text-muted-foreground">No active alerts for your monitored profile.</p>
          </div>
        )}

        <div className="space-y-3">
          {active.map(alert => {
            const Icon = alertIcons[alert.type];
            const style = alertStyle[alert.severity];
            const isExpanded = expandedId === alert.id;
            return (
              <div key={alert.id} className={`card-elevated rounded-xl border border-border border-l-2 ${style.border}`}>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <Icon size={15} className={`${style.icon} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground">{alert.title}</p>
                        <button onClick={() => dismiss(alert.id)} className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0" title="Dismiss">
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-2">{alert.body}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${style.badge}`}>{alert.severity}</span>
                        <span className="text-2xs text-muted-foreground">{alert.time} UTC</span>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : alert.id)}
                          className="text-2xs text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1 ml-auto"
                        >
                          {isExpanded ? 'Less detail' : 'View exposure detail'}
                          <ChevronRight size={10} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border/50 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {alert.affectedExposure && (
                      <div className="flex items-start gap-2">
                        <Building2 size={12} className="text-warning flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Affected Exposure</p>
                          <p className="text-xs text-foreground">{alert.affectedExposure}</p>
                        </div>
                      </div>
                    )}
                    {alert.clientRelevance && (
                      <div className="flex items-start gap-2">
                        <MapPin size={12} className="text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Client Relevance</p>
                          <p className="text-xs text-foreground">{alert.clientRelevance}</p>
                        </div>
                      </div>
                    )}
                    {alert.recommendedAction && (
                      <div className="flex items-start gap-2">
                        <CheckCircle2 size={12} className="text-success flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Recommended Action</p>
                          <p className="text-xs text-foreground">{alert.recommendedAction}</p>
                        </div>
                      </div>
                    )}
                    {alert.operationalImpact && (
                      <div className="flex items-start gap-2">
                        <TrendingUp size={12} className="text-danger flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Operational Impact</p>
                          <p className="text-xs text-foreground">{alert.operationalImpact}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {dismissed.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Dismissed ({dismissed.length})</p>
            <div className="space-y-2">
              {dismissed.map(alert => (
                <div key={alert.id} className="card-elevated rounded-xl border border-border p-3 opacity-50 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{alert.title}</p>
                  <button onClick={() => restore(alert.id)} className="text-2xs text-primary hover:text-primary/80 transition-colors">Restore</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
